// src/web/client/src/hooks/useShopSettings.js
//
// The signed-in store's settings, loaded once (GET /api/shops?shop_domain=) and
// saved with ONE call per change (PUT /api/shops/:domain with the whole settings
// object — the shape the Settings page has always sent). The Automations cards
// share one instance of this, so every switch on the page reads and writes the
// same object and no card overwrites another card's change.
//
// update(patch) is optimistic: the switch moves at once; on failure it moves
// back and the error is returned to the caller for a toast.
//
// applyLocal(patch) merges a patch the SERVER has already saved (e.g. POST
// /templates/turn-on/:type sets the type's setting itself) into the loaded
// object, so the next update() sends it along — no request of its own.

import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '@/constants';
import { authenticatedFetch, SubscriptionRequiredError } from '@/utils/authenticatedFetch';
import { getShopDomain } from '@/utils/shopContext';

export function useShopSettings() {
  const domain = getShopDomain();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const latest = useRef(null);

  useEffect(() => {
    let alive = true;
    if (!domain) { setLoading(false); return undefined; }
    (async () => {
      try {
        const res = await authenticatedFetch(`${API_BASE_URL}/shops?shop_domain=${encodeURIComponent(domain)}`);
        const data = res.ok ? await res.json().catch(() => null) : null;
        const found = Array.isArray(data?.shops) ? data.shops[0] || null : null;
        if (alive) { latest.current = found; setShop(found); setError(res.ok ? null : new Error(`HTTP ${res.status}`)); }
      } catch (err) {
        if (alive && !(err instanceof SubscriptionRequiredError)) setError(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [domain]);

  // Merge a patch into settings (one level deep for platform_emails) and save.
  const update = useCallback(async (patch) => {
    const current = latest.current;
    if (!current) return { ok: false, error: new Error('Shop not loaded') };
    const nextSettings = {
      ...(current.settings || {}),
      ...patch,
      ...(patch.platform_emails ? { platform_emails: { ...(current.settings?.platform_emails || {}), ...patch.platform_emails } } : {}),
    };
    const next = { ...current, settings: nextSettings };
    latest.current = next;
    setShop(next);
    setSaving(true);
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/shops/${encodeURIComponent(current.shop_domain)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: nextSettings }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return { ok: true };
    } catch (err) {
      latest.current = current;
      setShop(current);
      return { ok: false, error: err };
    } finally {
      setSaving(false);
    }
  }, []);

  const applyLocal = useCallback((patch) => {
    const current = latest.current;
    if (!current) return;
    const next = { ...current, settings: { ...(current.settings || {}), ...patch } };
    latest.current = next;
    setShop(next);
  }, []);

  return { shop, settings: shop?.settings || null, loading, error, saving, update, applyLocal, domain };
}
